/**
 * Compliance Documents Tab Component
 * Combines Provider Survey/Facility Information Sheet and 
 * Shaken Baby Syndrome/Child Maltreatment Policy
 */
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, Printer, Save, ClipboardList, ShieldAlert, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AGE_GROUPS = [
  { id: "infant", label: "Infant" },
  { id: "young_toddler", label: "Young Toddler" },
  { id: "older_toddler", label: "Older Toddler" },
  { id: "preschool", label: "Preschool" },
  { id: "young_school_age", label: "Young School-Age" },
  { id: "older_school_age", label: "Older School-Age" }
];

const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export const ComplianceDocumentsTab = ({ centerName = "", organization = {} }) => {
  const [activeTab, setActiveTab] = useState("facility_info");
  const [saving, setSaving] = useState(false);
  const [savedDocuments, setSavedDocuments] = useState([]);

  // Provider Survey / Facility Information Form
  const [facilityForm, setFacilityForm] = useState({
    facilityName: centerName || organization?.name || "",
    facilityAddress: organization?.address || "",
    email: organization?.email || "",
    phone: organization?.phone || "",
    licenseNumber: organization?.licenseNumber || "",
    currentDirector: "",
    designatedPersonInCharge: "",
    designeeHasFileAccess: "",
    totalChildrenEnrolled: "",
    maxChildrenServed: "",
    ageGroups: [],
    operatingHours: DAYS_OF_WEEK.reduce((acc, day) => ({ ...acc, [day]: "" }), {}),
    monthsOfOperation: "all",
    monthsIfMultiple: "",
    administersMedication: "",
    preparesAndServesMeals: "",
    poolOnSite: "",
    poolLocation: "",
    childrenGoSwimming: "",
    childrenGoOnWalks: "",
    walkLocations: "",
    childrenGoOnFieldTrips: "",
    fieldTripLocations: "",
    providesTransportation: "",
    numberOfVehicles: "",
    vehicleTypes: "",
    staffEmployed: "",
    seasonalStaff: "",
    volunteers: "",
    significantChanges: "",
    changesDescription: "",
    completedByName: "",
    completedByTitle: "",
    completedByDate: new Date().toISOString().split('T')[0]
  });

  // Shaken Baby Syndrome Policy Form
  const [policyForm, setPolicyForm] = useState({
    centerName: centerName || organization?.name || "",
    licenseNumber: organization?.licenseNumber || "",
    centerAddress: organization?.address || "",
    directorAdministrator: "",
    policyEffectiveDate: new Date().toISOString().split('T')[0],
    lastReviewedDate: new Date().toISOString().split('T')[0],
    cpsAgencyName: "",
    cpsPhoneNumber: "",
    internalSupportContact: "",
    staffAcknowledgementName: "",
    staffAcknowledgementPosition: "",
    staffAcknowledgementDate: new Date().toISOString().split('T')[0],
    directorName: "",
    directorSignatureDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchSavedDocuments();
  }, []);

  useEffect(() => {
    if (centerName || organization?.name) {
      setFacilityForm(prev => ({
        ...prev,
        facilityName: centerName || organization?.name || prev.facilityName,
        facilityAddress: organization?.address || prev.facilityAddress,
        email: organization?.email || prev.email,
        phone: organization?.phone || prev.phone,
        licenseNumber: organization?.licenseNumber || prev.licenseNumber
      }));
      setPolicyForm(prev => ({
        ...prev,
        centerName: centerName || organization?.name || prev.centerName,
        licenseNumber: organization?.licenseNumber || prev.licenseNumber,
        centerAddress: organization?.address || prev.centerAddress
      }));
    }
  }, [centerName, organization]);

  const fetchSavedDocuments = async () => {
    try {
      const response = await axios.get(`${API}/compliance-documents`);
      setSavedDocuments(response.data || []);
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
  };

  const handleFacilityChange = (field, value) => {
    setFacilityForm(prev => ({ ...prev, [field]: value }));
  };

  const handlePolicyChange = (field, value) => {
    setPolicyForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAgeGroupChange = (groupId, checked) => {
    setFacilityForm(prev => ({
      ...prev,
      ageGroups: checked 
        ? [...prev.ageGroups, groupId]
        : prev.ageGroups.filter(id => id !== groupId)
    }));
  };

  const handleOperatingHoursChange = (day, value) => {
    setFacilityForm(prev => ({
      ...prev,
      operatingHours: { ...prev.operatingHours, [day]: value }
    }));
  };

  const handleSaveFacilityForm = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/compliance-documents/facility-info`, facilityForm);
      toast.success("Provider Survey / Facility Information saved!");
      fetchSavedDocuments();
    } catch (error) {
      toast.error("Failed to save document");
    } finally {
      setSaving(false);
    }
  };

  const handleSavePolicyForm = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/compliance-documents/sbs-policy`, policyForm);
      toast.success("SBS/Child Maltreatment Policy saved!");
      fetchSavedDocuments();
    } catch (error) {
      toast.error("Failed to save document");
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6">
      <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 shadow-lg">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl text-purple-800 flex items-center gap-3">
                <ClipboardList className="w-7 h-7" />
                Compliance Documents
              </CardTitle>
              <p className="text-purple-600 text-sm mt-1">Required compliance forms with auto-filled center information</p>
            </div>
            <Button onClick={handlePrint} variant="outline" className="border-purple-300 text-purple-600 hover:bg-purple-50">
              <Printer className="w-4 h-4 mr-1" />
              Print
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid grid-cols-2 w-full max-w-lg mb-6">
              <TabsTrigger value="facility_info" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white">
                <FileText className="w-4 h-4 mr-2" />
                Provider Survey
              </TabsTrigger>
              <TabsTrigger value="sbs_policy" className="data-[state=active]:bg-pink-500 data-[state=active]:text-white">
                <ShieldAlert className="w-4 h-4 mr-2" />
                SBS/Maltreatment Policy
              </TabsTrigger>
            </TabsList>

            {/* Provider Survey / Facility Information Tab */}
            <TabsContent value="facility_info" className="space-y-6">
              <div className="bg-white p-4 rounded-lg border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-1">Provider Survey / Facility Information Sheet</h3>
                <p className="text-sm text-gray-500 mb-4">Child Care Centers: Certification Inspection Instrument (Renewal)</p>

                {/* Facility Identification */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <Label className="text-gray-700">Facility Name *</Label>
                    <Input
                      value={facilityForm.facilityName}
                      onChange={(e) => handleFacilityChange("facilityName", e.target.value)}
                      className="mt-1 bg-purple-50"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">License Number</Label>
                    <Input
                      value={facilityForm.licenseNumber}
                      onChange={(e) => handleFacilityChange("licenseNumber", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-gray-700">Facility Address</Label>
                    <Input
                      value={facilityForm.facilityAddress}
                      onChange={(e) => handleFacilityChange("facilityAddress", e.target.value)}
                      className="mt-1 bg-purple-50"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Email</Label>
                    <Input
                      type="email"
                      value={facilityForm.email}
                      onChange={(e) => handleFacilityChange("email", e.target.value)}
                      className="mt-1 bg-purple-50"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Phone</Label>
                    <Input
                      value={facilityForm.phone}
                      onChange={(e) => handleFacilityChange("phone", e.target.value)}
                      className="mt-1 bg-purple-50"
                    />
                  </div>
                </div>

                {/* Leadership */}
                <h4 className="font-medium text-purple-700 mb-3 border-t pt-4">Leadership & Administration</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <Label className="text-gray-700">Current Director or Primary Staff Person</Label>
                    <Input
                      value={facilityForm.currentDirector}
                      onChange={(e) => handleFacilityChange("currentDirector", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Designated Person in Charge</Label>
                    <Input
                      value={facilityForm.designatedPersonInCharge}
                      onChange={(e) => handleFacilityChange("designatedPersonInCharge", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Does Designee have access to all locked files when in charge?</Label>
                    <Select value={facilityForm.designeeHasFileAccess} onValueChange={(v) => handleFacilityChange("designeeHasFileAccess", v)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Capacity */}
                <h4 className="font-medium text-purple-700 mb-3 border-t pt-4">Capacity & Demographics</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-gray-700">Total # of children currently enrolled</Label>
                    <Input
                      type="number"
                      value={facilityForm.totalChildrenEnrolled}
                      onChange={(e) => handleFacilityChange("totalChildrenEnrolled", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Maximum # of children served at any given time</Label>
                    <Input
                      type="number"
                      value={facilityForm.maxChildrenServed}
                      onChange={(e) => handleFacilityChange("maxChildrenServed", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="mb-6">
                  <Label className="text-gray-700 mb-2 block">Ages of children in care (check all applicable)</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {AGE_GROUPS.map(group => (
                      <div key={group.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={group.id}
                          checked={facilityForm.ageGroups.includes(group.id)}
                          onCheckedChange={(checked) => handleAgeGroupChange(group.id, checked)}
                        />
                        <label htmlFor={group.id} className="text-sm text-gray-700">{group.label}</label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Operating Hours */}
                <h4 className="font-medium text-purple-700 mb-3 border-t pt-4">Days and Hours of Operation</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
                  {DAYS_OF_WEEK.map(day => (
                    <div key={day}>
                      <Label className="text-gray-700 text-xs">{day}</Label>
                      <Input
                        value={facilityForm.operatingHours[day]}
                        onChange={(e) => handleOperatingHoursChange(day, e.target.value)}
                        className="mt-1 text-sm"
                        placeholder="e.g., 7AM-6PM or N/A"
                      />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <Label className="text-gray-700">Months of Operation</Label>
                    <Select value={facilityForm.monthsOfOperation} onValueChange={(v) => handleFacilityChange("monthsOfOperation", v)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Year</SelectItem>
                        <SelectItem value="multiple">Multiple (specify)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {facilityForm.monthsOfOperation === "multiple" && (
                    <div>
                      <Label className="text-gray-700">If multiple, please list</Label>
                      <Input
                        value={facilityForm.monthsIfMultiple}
                        onChange={(e) => handleFacilityChange("monthsIfMultiple", e.target.value)}
                        className="mt-1"
                        placeholder="e.g., Sept-May"
                      />
                    </div>
                  )}
                </div>

                {/* Program Operations */}
                <h4 className="font-medium text-purple-700 mb-3 border-t pt-4">Programmatic Operations</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  <div>
                    <Label className="text-gray-700">Does program administer medication?</Label>
                    <Select value={facilityForm.administersMedication} onValueChange={(v) => handleFacilityChange("administersMedication", v)}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-700">Does program prepare and serve meals?</Label>
                    <Select value={facilityForm.preparesAndServesMeals} onValueChange={(v) => handleFacilityChange("preparesAndServesMeals", v)}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-700">Is there a pool on site?</Label>
                    <Select value={facilityForm.poolOnSite} onValueChange={(v) => handleFacilityChange("poolOnSite", v)}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {facilityForm.poolOnSite === "yes" && (
                    <div>
                      <Label className="text-gray-700">Pool Location</Label>
                      <Input
                        value={facilityForm.poolLocation}
                        onChange={(e) => handleFacilityChange("poolLocation", e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  )}
                  <div>
                    <Label className="text-gray-700">Do children go swimming?</Label>
                    <Select value={facilityForm.childrenGoSwimming} onValueChange={(v) => handleFacilityChange("childrenGoSwimming", v)}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-700">Do children go on walks off premises?</Label>
                    <Select value={facilityForm.childrenGoOnWalks} onValueChange={(v) => handleFacilityChange("childrenGoOnWalks", v)}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {facilityForm.childrenGoOnWalks === "yes" && (
                    <div>
                      <Label className="text-gray-700">Walk Locations</Label>
                      <Input
                        value={facilityForm.walkLocations}
                        onChange={(e) => handleFacilityChange("walkLocations", e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  )}
                  <div>
                    <Label className="text-gray-700">Do children go on field trips?</Label>
                    <Select value={facilityForm.childrenGoOnFieldTrips} onValueChange={(v) => handleFacilityChange("childrenGoOnFieldTrips", v)}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {facilityForm.childrenGoOnFieldTrips === "yes" && (
                    <div>
                      <Label className="text-gray-700">Field Trip Locations</Label>
                      <Input
                        value={facilityForm.fieldTripLocations}
                        onChange={(e) => handleFacilityChange("fieldTripLocations", e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  )}
                  <div>
                    <Label className="text-gray-700">Is transportation provided?</Label>
                    <Select value={facilityForm.providesTransportation} onValueChange={(v) => handleFacilityChange("providesTransportation", v)}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {facilityForm.providesTransportation === "yes" && (
                    <>
                      <div>
                        <Label className="text-gray-700"># of Vehicles</Label>
                        <Input
                          type="number"
                          value={facilityForm.numberOfVehicles}
                          onChange={(e) => handleFacilityChange("numberOfVehicles", e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-700">Type(s) of Vehicle(s)</Label>
                        <Input
                          value={facilityForm.vehicleTypes}
                          onChange={(e) => handleFacilityChange("vehicleTypes", e.target.value)}
                          className="mt-1"
                        />
                      </div>
                    </>
                  )}
                </div>

                {/* Staffing */}
                <h4 className="font-medium text-purple-700 mb-3 border-t pt-4">Staffing & Changes</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <Label className="text-gray-700"># of staff employed</Label>
                    <Input
                      type="number"
                      value={facilityForm.staffEmployed}
                      onChange={(e) => handleFacilityChange("staffEmployed", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700"># of seasonal staff</Label>
                    <Input
                      type="number"
                      value={facilityForm.seasonalStaff}
                      onChange={(e) => handleFacilityChange("seasonalStaff", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700"># of volunteers</Label>
                    <Input
                      type="number"
                      value={facilityForm.volunteers}
                      onChange={(e) => handleFacilityChange("volunteers", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <Label className="text-gray-700">Any significant facility changes since last certificate?</Label>
                    <Select value={facilityForm.significantChanges} onValueChange={(v) => handleFacilityChange("significantChanges", v)}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {facilityForm.significantChanges === "yes" && (
                    <div className="md:col-span-2">
                      <Label className="text-gray-700">Please describe changes</Label>
                      <Textarea
                        value={facilityForm.changesDescription}
                        onChange={(e) => handleFacilityChange("changesDescription", e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  )}
                </div>

                {/* Signature */}
                <h4 className="font-medium text-purple-700 mb-3 border-t pt-4">Form Completion</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-gray-700">Name of person completing form</Label>
                    <Input
                      value={facilityForm.completedByName}
                      onChange={(e) => handleFacilityChange("completedByName", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Title</Label>
                    <Input
                      value={facilityForm.completedByTitle}
                      onChange={(e) => handleFacilityChange("completedByTitle", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Date</Label>
                    <Input
                      type="date"
                      value={facilityForm.completedByDate}
                      onChange={(e) => handleFacilityChange("completedByDate", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={handleSaveFacilityForm}
                  disabled={saving}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-8"
                >
                  {saving ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Saving...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" />Save Provider Survey</>
                  )}
                </Button>
              </div>
            </TabsContent>

            {/* SBS/Child Maltreatment Policy Tab */}
            <TabsContent value="sbs_policy" className="space-y-6">
              <div className="bg-white p-4 rounded-lg border border-pink-200">
                <h3 className="font-semibold text-pink-800 mb-1">How to Identify & Prevent Shaken Baby Syndrome, Abusive Head Trauma & Child Maltreatment Policy</h3>
                <p className="text-sm text-gray-500 mb-4">Required policy document for childcare centers</p>

                {/* Center Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <Label className="text-gray-700">Center Name *</Label>
                    <Input
                      value={policyForm.centerName}
                      onChange={(e) => handlePolicyChange("centerName", e.target.value)}
                      className="mt-1 bg-pink-50"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">License Number</Label>
                    <Input
                      value={policyForm.licenseNumber}
                      onChange={(e) => handlePolicyChange("licenseNumber", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-gray-700">Center Address</Label>
                    <Input
                      value={policyForm.centerAddress}
                      onChange={(e) => handlePolicyChange("centerAddress", e.target.value)}
                      className="mt-1 bg-pink-50"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Director/Administrator</Label>
                    <Input
                      value={policyForm.directorAdministrator}
                      onChange={(e) => handlePolicyChange("directorAdministrator", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Policy Effective Date</Label>
                    <Input
                      type="date"
                      value={policyForm.policyEffectiveDate}
                      onChange={(e) => handlePolicyChange("policyEffectiveDate", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Last Reviewed/Updated</Label>
                    <Input
                      type="date"
                      value={policyForm.lastReviewedDate}
                      onChange={(e) => handlePolicyChange("lastReviewedDate", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Policy Content - Read Only Sections */}
                <div className="space-y-4 mb-6">
                  <div className="bg-gray-50 p-4 rounded-lg border">
                    <h4 className="font-medium text-gray-800 mb-2">1. Purpose</h4>
                    <p className="text-sm text-gray-600">This policy ensures all staff understand how to identify, prevent, and report Shaken Baby Syndrome (SBS), Abusive Head Trauma (AHT), and child maltreatment. Our center is committed to maintaining a safe environment for all children.</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg border">
                    <h4 className="font-medium text-gray-800 mb-2">2. Definitions</h4>
                    <ul className="text-sm text-gray-600 space-y-2">
                      <li><strong>Shaken Baby Syndrome (SBS):</strong> A form of inflicted traumatic brain injury caused by violently shaking an infant or small child.</li>
                      <li><strong>Abusive Head Trauma (AHT):</strong> Head injuries resulting from abuse, including shaking, impact, or both.</li>
                      <li><strong>Child Maltreatment:</strong> Includes physical abuse, emotional abuse, sexual abuse, neglect, abandonment, and exploitation.</li>
                    </ul>
                  </div>

                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <h4 className="font-medium text-red-800 mb-2">3. Policy Statement</h4>
                    <p className="text-sm text-red-700">This center maintains a <strong>ZERO TOLERANCE</strong> policy for any form of abuse or maltreatment. Staff are prohibited from shaking, striking, slapping, or using excessive force on any child.</p>
                  </div>

                  <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                    <h4 className="font-medium text-amber-800 mb-2">4. Identifying Signs of SBS or AHT</h4>
                    <ul className="text-sm text-amber-700 list-disc pl-5 space-y-1">
                      <li>Vomiting or seizures</li>
                      <li>Difficulty breathing</li>
                      <li>Unusual sleepiness or lethargy</li>
                      <li>Poor feeding</li>
                      <li>Bruising on head or neck</li>
                      <li>Sudden behavioral changes</li>
                    </ul>
                  </div>

                  <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                    <h4 className="font-medium text-amber-800 mb-2">5. Identifying Signs of Child Maltreatment</h4>
                    <ul className="text-sm text-amber-700 list-disc pl-5 space-y-1">
                      <li>Unexplained injuries or bruising</li>
                      <li>Poor hygiene or untreated medical needs</li>
                      <li>Fear of caregivers</li>
                      <li>Withdrawal or aggression</li>
                      <li>Sudden developmental changes</li>
                    </ul>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <h4 className="font-medium text-green-800 mb-2">6. Prevention Strategies</h4>
                    <ul className="text-sm text-green-700 list-disc pl-5 space-y-1">
                      <li>Always support infant heads and necks - NEVER shake a child</li>
                      <li>Check basic needs when child cries excessively (hunger, diaper, temperature)</li>
                      <li>Use calming techniques: gentle rocking, soft sounds, swaddling</li>
                    </ul>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h4 className="font-medium text-blue-800 mb-2">7. Staff Stress Management</h4>
                    <p className="text-sm text-blue-700">Staff should seek assistance from coworkers or supervisors when feeling overwhelmed and use coping strategies to manage stress.</p>
                  </div>
                </div>

                {/* Customizable Reporting Section */}
                <h4 className="font-medium text-pink-700 mb-3 border-t pt-4">8. Reporting Requirements (Customize)</h4>
                <div className="bg-pink-50 p-4 rounded-lg border border-pink-200 mb-6">
                  <p className="text-sm text-pink-700 mb-4">All staff are mandated reporters and must report suspected abuse immediately to the center director and Child Protective Services.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-700">Child Protective Services Agency Name</Label>
                      <Input
                        value={policyForm.cpsAgencyName}
                        onChange={(e) => handlePolicyChange("cpsAgencyName", e.target.value)}
                        className="mt-1"
                        placeholder="e.g., County CPS"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-700">CPS Phone Number</Label>
                      <Input
                        value={policyForm.cpsPhoneNumber}
                        onChange={(e) => handlePolicyChange("cpsPhoneNumber", e.target.value)}
                        className="mt-1"
                        placeholder="e.g., 1-800-XXX-XXXX"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-gray-700">Internal Support Contact</Label>
                      <Input
                        value={policyForm.internalSupportContact}
                        onChange={(e) => handlePolicyChange("internalSupportContact", e.target.value)}
                        className="mt-1"
                        placeholder="Name and contact info for internal support"
                      />
                    </div>
                  </div>
                </div>

                {/* Staff Acknowledgement */}
                <h4 className="font-medium text-pink-700 mb-3 border-t pt-4">Staff Acknowledgement</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <Label className="text-gray-700">Staff Name</Label>
                    <Input
                      value={policyForm.staffAcknowledgementName}
                      onChange={(e) => handlePolicyChange("staffAcknowledgementName", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Position</Label>
                    <Input
                      value={policyForm.staffAcknowledgementPosition}
                      onChange={(e) => handlePolicyChange("staffAcknowledgementPosition", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Date</Label>
                    <Input
                      type="date"
                      value={policyForm.staffAcknowledgementDate}
                      onChange={(e) => handlePolicyChange("staffAcknowledgementDate", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Director Verification */}
                <h4 className="font-medium text-pink-700 mb-3 border-t pt-4">Administrator Verification</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700">Director Name</Label>
                    <Input
                      value={policyForm.directorName}
                      onChange={(e) => handlePolicyChange("directorName", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Date</Label>
                    <Input
                      type="date"
                      value={policyForm.directorSignatureDate}
                      onChange={(e) => handlePolicyChange("directorSignatureDate", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={handleSavePolicyForm}
                  disabled={saving}
                  className="bg-pink-600 hover:bg-pink-700 text-white px-8"
                >
                  {saving ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Saving...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" />Save Policy Document</>
                  )}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Saved Documents Summary */}
      {savedDocuments.length > 0 && (
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg text-gray-700 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Saved Compliance Documents ({savedDocuments.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {savedDocuments.slice(0, 5).map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                  <div>
                    <span className="font-medium text-gray-800">{doc.documentType === 'facility_info' ? 'Provider Survey' : 'SBS/Maltreatment Policy'}</span>
                    <span className="text-sm text-gray-500 ml-3">Saved: {new Date(doc.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ComplianceDocumentsTab;
